import List from "@mui/material/List"
import Input from "@mui/material/Input"
import Avatar from "@mui/material/Avatar"
import * as api from '../../../services/api'
import ListItem from "@mui/material/ListItem"
import { useNavigate } from "react-router-dom"
import { useCore } from "../../../hooks/useCore"
import IconButton from "@mui/material/IconButton"
import InputLabel from "@mui/material/InputLabel"
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import FormControl from "@mui/material/FormControl"
import ListItemText from "@mui/material/ListItemText"
import { toast, ToastContainer } from 'react-toastify'
import ListItemButton from "@mui/material/ListItemButton"
import InputAdornment from "@mui/material/InputAdornment"
import CircularProgress from "@mui/material/CircularProgress"
import ListItemAvatar from "@mui/material/ListItemAvatar"
import { Paginator } from "../../../components/Paginator"
import ModalDelete from "../../../components/ModalDelete"
import DescriptionIcon from '@mui/icons-material/Description'
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { ListReportTemplateProps, ReportTemplateProps } from "../../../types/reportTemplate"
import { useDebounce } from "../../../utils/useDebounce"

export default function ReportTemplates(): JSX.Element {
  const navigate = useNavigate()
  const { setPathTitleBar, setTitleBar } = useCore()

  const [templates, setTemplates] = useState<ReportTemplateProps[]>([])
  const [open, setOpen] = useState<boolean>(false)
  const [idDelete, setIdDelete] = useState<string | undefined>()
  const [searchValue, setSearchValue] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [count, setCount] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const debouncedSearch = useDebounce(searchValue, 500)
  const requestIdRef = useRef(0)

  const buildListUrl = (page: number, termRaw: string) => {
    const base = `product/report-template/list/?page=${page}`
    const term = (termRaw || '').trim()
    return term.length >= 2 ? `${base}&search=${encodeURIComponent(term)}` : base
  }

  const getTemplates = async (url: string) => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    const response = await api.get(url) as ListReportTemplateProps
    if (requestId !== requestIdRef.current) return // resposta de um request antigo — ignora
    setTemplates(response.content.results)
    setCount(response.content.count)
    setLoading(false)
  }

  const handleChangePage = async (event: React.ChangeEvent<unknown>, value: number) => {
    setLoading(true)
    setPageNumber(value)
    await getTemplates(buildListUrl(value, debouncedSearch))
  }

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  function handleModalOpen(id: string) {
    setIdDelete(id)
    setOpen(true)
  }

  useEffect(() => {
    const term = debouncedSearch.trim()
    if (term.length === 0) {
      setLoading(true)
      setPageNumber(1)
      getTemplates(buildListUrl(1, ''))
    } else if (term.length >= 2) {
      setLoading(true)
      setPageNumber(1)
      getTemplates(buildListUrl(1, term))
    }
  }, [debouncedSearch])

  const handleModalClose = () => setOpen(false)

  const handleDelete = async () => {
    setLoading(true)
    const response = await api.destroy(`product/report-template/destroy/${idDelete}/`) as {[key:string]: any}
    if (response.status === 204) {
      toast.success("Template excluído com sucesso!", {
        position: toast.POSITION.TOP_RIGHT
      })
      await getTemplates(buildListUrl(pageNumber, debouncedSearch))
    } else {
      toast.error("Falha ao tentar excluir template!", {
        position: toast.POSITION.TOP_RIGHT
      })
    }
    handleModalClose()
    setLoading(false)
  }

  useLayoutEffect(() => {
    setTitleBar("Templates de extrato")
    setPathTitleBar("/new-report-template")
  }, [setTitleBar, setPathTitleBar])

  return (
    <>
      <ToastContainer />
      <ModalDelete open={open} textBody="Tem certeza que deseja excluir esse template?" handleModalClose={handleModalClose} handleDelete={handleDelete} loading={loading} />

      <List dense disablePadding sx={{ width: '100%' }}>
        <ListItem
          key='search'
          disablePadding
        >
          <FormControl fullWidth sx={{ m: 1 }} variant="standard">
            <InputLabel htmlFor="standard-adornment-amount">Pesquisar</InputLabel>
            <Input
              id="input-search"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              onChange={handleChangeSearch}
            />
          </FormControl>
        </ListItem>

        {loading ? (
          <ListItem disablePadding>
            <ListItemAvatar>
              <CircularProgress size={24} />
            </ListItemAvatar>
            <ListItemText primary="Carregando templates…" />
          </ListItem>
        ) : templates?.map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={() => {handleModalOpen(item.id)}}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemButton onClick={() => navigate(`/report-template/${item.id}`)}>
                <ListItemAvatar>
                  <Avatar>
                    <DescriptionIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={item.name} secondary={item.version} />
              </ListItemButton>
            </ListItem>
          ))
        }
      </List>
      <Paginator count={count} pageNumber={pageNumber} onChange={handleChangePage} />
    </>
  )
}
